import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';

const isValidRecord = (value: any, defaultValue: Record<string, boolean>): boolean => {
  if (typeof value !== 'object' || value === null) return false;
  return Object.entries(value).every(([key, val]) => typeof key === 'string' && typeof val === 'boolean');
};

export default function useStickyState<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
  const location = useLocation();
  const [value, setValue] = useState<T>(() => {
    try {
      const stickyValue = localStorage.getItem(key);
      if (stickyValue === null) {
        return defaultValue;
      }
      const parsedValue = JSON.parse(stickyValue) as T;

      // Special handling for Record<string, boolean>
      if (
        typeof defaultValue === 'object' &&
        defaultValue !== null &&
        Object.keys(defaultValue as Record<string, any>).every(
          (k) => typeof (defaultValue as Record<string, any>)[k] === 'boolean'
        )
      ) {
        if (isValidRecord(parsedValue, defaultValue as Record<string, boolean>)) {
          return parsedValue;
        }
        localStorage.removeItem(key);
        return defaultValue;
      }

      // Default type validation for other types
      if (typeof parsedValue === typeof defaultValue) {
        return parsedValue;
      }
      // If types don't match, fall back to default
      localStorage.removeItem(key);
      return defaultValue;
    } catch (err) {
      // If any error occurs (parsing, etc), remove the invalid data and use default
      localStorage.removeItem(key);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      console.error(`Failed to save value to localStorage for key: ${key}`, err);
    }
  }, [key, value]);

  useEffect(() => {
    try {
      const stickyValue = localStorage.getItem(key);
      if (stickyValue) {
        const storedValue = JSON.parse(stickyValue) as T;
        // Only update if the stored value is valid and different from current
        if (
          typeof defaultValue === 'object' &&
          defaultValue !== null &&
          Object.keys(defaultValue as Record<string, any>).every(
            (k) => typeof (defaultValue as Record<string, any>)[k] === 'boolean'
          )
        ) {
          if (
            isValidRecord(storedValue, defaultValue as Record<string, boolean>) &&
            JSON.stringify(storedValue) !== JSON.stringify(value)
          ) {
            setValue(storedValue);
          }
        } else if (
          typeof storedValue === typeof defaultValue &&
          JSON.stringify(storedValue) !== JSON.stringify(value)
        ) {
          setValue(storedValue);
        }
      }
    } catch (err) {
      // If there's any error reading/parsing, remove the invalid data
      localStorage.removeItem(key);
    }
  }, [key, location, value]);

  return [value, setValue];
}
